import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type { EnhancedMarketId, EnhancedReferenceTruck } from '#api/enhancements/enhancedTmsApiTypes'
import type {
  DeleteApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiArg,
  PostApiAdmintoolMarketsByMarketIdReferenceTrucksApiArg,
  PostApiAdmintoolMarketsByMarketIdReferenceTrucksApiResponse,
  PutApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiArg,
  PutApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiResponse,
} from '#api/tmsApiService.ts'
import { makeSerializedError } from '#features/ErrorHandling/errorHelpers'
import {
  _pushOrReplaceReferenceTruck,
  _setMarketStatus,
  _setReferenceTrucks,
  loadReferenceTrucksForMarket,
  type ReferenceTruckId,
} from '#store/referenceTrucksSlice.ts'
import { store } from '#store/root'

export const referenceTrucksObserver = (() => {
  const middleware = createListenerMiddleware()
  middleware.startListening({
    matcher: isAnyOf(loadReferenceTrucksForMarket),
    effect: async (action, _listenerApi) => {
      const state = store.getState()
      if (loadReferenceTrucksForMarket.match(action)) {
        const marketId = action.payload.marketId
        const marketState = state.referenceTrucks.byMarket[marketId]
        if (marketState?.status === 'loaded' && !action.payload?.forceReload) {
          return
        }

        try {
          store.dispatch(_setMarketStatus({ marketId, status: 'loading' }))
          const request = store.dispatch(
            enhancedTmsApiService.endpoints.getApiAdmintoolMarketsByMarketIdReferenceTrucks.initiate({
              marketId,
            }),
          )
          const result = await request.unwrap()

          if (result.listOfReferenceTrucks) {
            const validReferenceTrucks = result.listOfReferenceTrucks.filter((m) => m.referenceTruckId !== undefined) as EnhancedReferenceTruck[]
            store.dispatch(
              _setReferenceTrucks({
                marketId,
                referenceTrucks: validReferenceTrucks,
              }),
            )
            store.dispatch(_setMarketStatus({ marketId, status: 'loaded' }))
          }
        } catch (e) {
          store.dispatch(_setMarketStatus({ marketId, status: 'failed', error: makeSerializedError(e) }))
        }
      }
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.postApiAdmintoolMarketsByMarketIdReferenceTrucks.matchFulfilled,
    effect: (action, api) => {
      console.log('Done POST /admintool/referenceTrucks', action, api)
      const originalArgs = action.meta.arg.originalArgs as PostApiAdmintoolMarketsByMarketIdReferenceTrucksApiArg
      const result = action.payload as PostApiAdmintoolMarketsByMarketIdReferenceTrucksApiResponse

      if (!result.referenceTruck) {
        console.warn('No reference truck returned from POST /admintool/referenceTrucks')
        return
      }

      api.dispatch(
        _pushOrReplaceReferenceTruck({
          marketId: originalArgs.marketId as EnhancedMarketId,
          referenceTruckId: result.referenceTruck.referenceTruckId as ReferenceTruckId,
          referenceTruck: result.referenceTruck as EnhancedReferenceTruck,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.putApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckId.matchFulfilled,
    effect: (action, api) => {
      console.log('Done PUT /admintool/referenceTrucks', action, api)
      const originalArgs = action.meta.arg.originalArgs as PutApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiArg
      const result = action.payload as PutApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiResponse
      api.dispatch(
        _pushOrReplaceReferenceTruck({
          marketId: originalArgs.marketId as EnhancedMarketId,
          referenceTruckId: originalArgs.referenceTruckId as ReferenceTruckId,
          referenceTruck: result.referenceTruck as EnhancedReferenceTruck,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.deleteApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckId.matchFulfilled,
    effect: (action, api) => {
      console.log('Done DELETE /admintool/referenceTrucks', action, api)
      const originalArgs = action.meta.arg.originalArgs as DeleteApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiArg
      api.dispatch(
        _pushOrReplaceReferenceTruck({
          marketId: originalArgs.marketId as EnhancedMarketId,
          referenceTruckId: originalArgs.referenceTruckId as ReferenceTruckId,
          referenceTruck: null,
        }),
      )
    },
  })

  return middleware
})()
